import { useState } from 'react'
import clsx from 'clsx'
import { Icon } from '@/components'
import { ChargeSegment } from './ChargeSessionDetails.utils'
import { RATE_PERIOD_BACKGROUNDS } from './UpcomingRatesCard'

// Figma's untokenized caption grey for "CHARGING WINDOW" / "RATE TIERS"
const MICRO_LABEL_CLASSES =
  'text-[10px] font-bold leading-normal tracking-[0.8px] text-themed-grey-500'

type Props = {
  segments: ChargeSegment[]
  chargingWindow: string | null
  defaultExpanded?: boolean
}

export default function ChargeSessionDetails({
  segments,
  chargingWindow,
  defaultExpanded = true,
}: Props) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <div className="flex flex-col w-full gap-2">
      <button
        id="charge-forecast-v2-session-details-toggle"
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        className="flex items-center w-full gap-2 p-0 text-left bg-transparent border-0 cursor-pointer"
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 bg-themed-green-100">
          <Icon name="Leaf" color="green-700" size={16} aria-hidden />
        </span>
        <span className="flex-1 text-sm font-bold leading-5 text-themed-grey-900">
          Charge Session Details
        </span>
        <Icon
          name="ChevronDown"
          color="grey-400"
          size={16}
          strokeWidth={1.5}
          rotation={expanded ? 0 : -90}
          className="shrink-0 transition-transform"
          aria-hidden
        />
      </button>
      {expanded && (
        <>
          {chargingWindow && (
            <div className="flex flex-col gap-0.5">
              <span className={MICRO_LABEL_CLASSES}>CHARGING WINDOW</span>
              <span className="text-xs font-bold leading-5 text-themed-grey-900">
                {chargingWindow}
              </span>
            </div>
          )}
          {segments.map((segment) => (
            <SegmentCard key={segment.id} segment={segment} />
          ))}
        </>
      )}
    </div>
  )
}

function SegmentCard({ segment }: { segment: ChargeSegment }) {
  return (
    <div
      className={clsx(
        'box-border flex items-start w-full gap-2 p-4 border border-solid rounded-lg',
        segment.isCurrent
          ? 'bg-themed-green-100 border-themed-green-700'
          : 'bg-white border-themed-grey-200',
      )}
    >
      <span className="flex shrink-0 w-4 h-4 pt-0.5">
        {segment.isCharging ? (
          <Icon
            name="Zap"
            color="grey-900"
            size={16}
            strokeWidth={1.5}
            aria-hidden
          />
        ) : (
          <Icon
            name="Pause"
            color="purple-500"
            size={16}
            strokeWidth={1.5}
            aria-hidden
          />
        )}
      </span>
      <div className="flex flex-col flex-1 min-w-0 gap-1.5">
        <div className="flex items-center gap-2">
          <span className="flex-1 text-xs font-bold leading-5 text-themed-grey-900">
            {segment.title}
          </span>
          {segment.isCurrent && (
            <span className="text-xs font-bold text-themed-green-700">
              Current
            </span>
          )}
        </div>
        <span className="text-xs font-medium leading-5 text-themed-grey-500">
          {segment.timeRange}
        </span>
        {segment.tiers.length > 0 && (
          <>
            <span className={MICRO_LABEL_CLASSES}>RATE TIERS</span>
            {segment.tiers.map((tier) => (
              <div
                key={`${tier.label}-${tier.timeRange}`}
                className="flex items-center w-full gap-1.5 text-[11px] leading-normal"
              >
                <span
                  aria-hidden
                  className={clsx(
                    'w-2 h-2 rounded-full shrink-0',
                    RATE_PERIOD_BACKGROUNDS[tier.period],
                  )}
                />
                <span className="font-medium text-themed-grey-500 whitespace-nowrap">
                  {tier.label}
                </span>
                <span className="flex-1" />
                <span className="text-themed-grey-500 whitespace-nowrap">
                  {tier.timeRange}
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}
